import config from 'config/config';

const TOKEN_STATUS = {
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
} as const;

type FailureReason =
  | 'SCRIPT_LOAD_FAILED'
  | 'RENDER_FAILED'
  | 'CHALLENGE_ERROR'
  | 'CHALLENGE_TIMEOUT'
  | 'CHALLENGE_CANCELLED'
  | 'UNKNOWN';

export type ChallengeReturnValue =
  | { status: typeof TOKEN_STATUS.SUCCESS; token: string }
  | {
      status: typeof TOKEN_STATUS.FAILURE;
      failure: { reason: FailureReason; message?: string; error?: unknown };
    };

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement | string, params: Record<string, any>) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';

function loadTurnstile(apiUrl: string): Promise<NonNullable<Window['turnstile']>> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile can only run in the browser'));
  }
  if (window.turnstile) return Promise.resolve(window.turnstile);

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;

    if (existing) {
      existing.addEventListener('load', () => {
        if (window.turnstile) resolve(window.turnstile);
        else reject(new Error('Turnstile loaded but window.turnstile is missing'));
      });
      existing.addEventListener('error', () => reject(new Error('Failed to load Turnstile script')));
      return;
    }

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = apiUrl;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile loaded but window.turnstile is missing'));
    };
    script.onerror = () => reject(new Error('Failed to load Turnstile script'));

    document.head.appendChild(script);
  });
}

function slugCData(input: string): string {
  // Turnstile cData: max 255 chars; A-Za-z0-9_- only
  return input
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '_')
    .slice(0, 255);
}

export async function getFrictionInit(
  action: 'login' | 'register',
  username: string,
  renderChallenge: (element?: HTMLElement) => void
): Promise<ChallengeReturnValue> {
  try {
    const { TURNSTILE_API_URL, TURNSTILE_SITE_KEY } = config;
    const turnstile = await loadTurnstile(TURNSTILE_API_URL);

    const wrapper = document.createElement('div');
    wrapper.style.height = '0px';
    wrapper.style.overflow = 'hidden';
    wrapper.style.transition = 'height 120ms ease';
    wrapper.style.willChange = 'height';
    wrapper.style.marginTop = '0px';
    wrapper.style.display = 'flex';
    wrapper.style.justifyContent = 'center';

    renderChallenge(wrapper);
    // Let React mount wrapper into the DOM
    await new Promise<void>((r) => requestAnimationFrame(() => r()));

    const TIMEOUT_MS = 60_000;

    const token = await new Promise<string>((resolve, reject) => {
      let widgetId: string | undefined;
      let settled = false;

      // Turnstile widget heights: normal/flexible = 65px, compact = 140px
      const TARGET_HEIGHT_PX = 65;

      const safeRemove = () => {
        if (!widgetId) return;
        try {
          turnstile.remove(widgetId);
        } catch {
          // swallow: Turnstile can throw if widget already removed / container changed
        }
      };

      const cleanup = () => {
        wrapper.style.height = '0px';
        wrapper.style.marginTop = '0px';
        safeRemove();
      };

      const timeout = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(Object.assign(new Error(), { code: 'CHALLENGE_TIMEOUT' }));
      }, TIMEOUT_MS);

      try {
        widgetId = turnstile.render(wrapper, {
          sitekey: TURNSTILE_SITE_KEY,
          appearance: 'interaction-only',
          theme: 'light',
          action,
          cData: slugCData(`${action}:${username}`),
          language: 'pl',

          // Expand only when Turnstile is about to show interactive UI
          'before-interactive-callback': () => {
            if (settled) return;
            wrapper.style.height = `${TARGET_HEIGHT_PX}px`;
            wrapper.style.marginTop = '16px';
          },

          callback: (t: string) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeout);
            resolve(t);
          },

          'error-callback': () => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeout);
            cleanup();
            reject(Object.assign(new Error(), { code: 'CHALLENGE_ERROR' }));
          },

          'expired-callback': () => {
            // Do nothing
          },
        });
      } catch (e) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        safeRemove();
        reject(
          Object.assign(e instanceof Error ? e : new Error(), {
            code: 'RENDER_FAILED',
          })
        );
      }
    });

    return { status: TOKEN_STATUS.SUCCESS, token };
  } catch (error) {
    // Map to your FailureReason set
    const code = (error as any)?.code as string | undefined;

    let reason: FailureReason;
    let message: string;

    switch (code) {
      case 'CHALLENGE_TIMEOUT':
        reason = 'CHALLENGE_TIMEOUT';
        message = 'Weryfikacja wygasła. Spróbuj ponownie';
        break;

      case 'RENDER_FAILED':
        reason = 'RENDER_FAILED';
        message = 'Wystąpił błąd podczas ładowania weryfikacji';
        break;

      case 'CHALLENGE_ERROR':
        reason = 'CHALLENGE_ERROR';
        message = 'Błąd weryfikacji';
        break;

      default: {
        const errorMessage = String((error as any)?.message || '').toLowerCase();

        if (errorMessage.includes('load turnstile')) {
          reason = 'SCRIPT_LOAD_FAILED';
        } else {
          reason = 'UNKNOWN';
        }

        message = 'Wystąpił błąd podczas ładowania weryfikacji';
      }
    }

    return {
      status: TOKEN_STATUS.FAILURE,
      failure: {
        reason,
        message,
        error,
      },
    };
  }
}

export const JSXElementFromHTMLElement = ({ htmlElement }: { htmlElement: HTMLElement }) => (
  <div
    ref={(element) => {
      if (!element) return;
      // Prevent repeated append on re-renders
      if (htmlElement.parentElement !== element) element.replaceChildren(htmlElement);
    }}
  />
);
